import { useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import { useStableQueries } from '@frontend/react-query-helpers';
import { useAppScopeStore } from '@frontend/scope';
import { Helpers, Queries, Types } from '..';

//#region Date Helpers (TODO: Move to a shared location as we have similar helpers in other place(/schedule/src/utils/date-helpers.ts))

const DATE_DISPLAY_FORMAT = 'MM/DD/YYYY';
export const DATETIME_FORMATS = {
  US_DATE: 'MM/DD/YYYY', // 12/31/2022
  ISO_DATE: 'YYYY-MM-DD', // 2025-06-13
  ISO_TIME: 'HH:mm:ss', // 2025-06-13T14:30:00
  ISO_DATETIME: 'YYYY-MM-DDTHH:mm:ss', // 2025-06-13T14:30:00
  ISO_DATETIME_WITH_MILLISECONDS: 'YYYY-MM-DDTHH:mm:ss.sss', // 2025-06-13T14:30:00.123
  ISO_DATETIME_WITH_MILLISECONDS_UTC: 'YYYY-MM-DDTHH:mm:ss.sss[Z]', // 2025-06-13T14:30:00.123Z
  ISO_DATETIME_WITH_OFFSET: 'YYYY-MM-DDTHH:mm:ssZ', // 2025-06-13T14:30:00-05:00
  HOURS_MINUTES: 'HH:mm',
};
const isValidDate = (date: dayjs.Dayjs): boolean => {
  // Check if the date is valid and not before the year 1900
  return date.isValid() && date.year() >= 1900;
};

const getDateByTimezone = (dateTime: dayjs.ConfigType, fromFormat: string, fromTimezone?: string): dayjs.Dayjs => {
  if (fromTimezone) {
    return dayjs.tz(dateTime, fromFormat, fromTimezone);
  }
  return dayjs(dateTime, fromFormat);
};

type GetCalendarStartAndEndDateTimeParams = {
  selectedCalendarDate: string;
  timezone: string;
  isOnboardingFlow?: boolean;
  customStartDateTime?: string;
  customEndDateTime?: string;
};

const getCalendarStartAndEndDateTime = ({
  selectedCalendarDate,
  timezone,
  isOnboardingFlow,
  customEndDateTime,
  customStartDateTime,
}: GetCalendarStartAndEndDateTimeParams) => {
  if (customEndDateTime && customStartDateTime) {
    return {
      startDateTime: dayjs(customStartDateTime).utc().tz(timezone).format(DATETIME_FORMATS.ISO_DATETIME_WITH_OFFSET),
      endDateTime: dayjs(customEndDateTime).utc().tz(timezone).format(DATETIME_FORMATS.ISO_DATETIME_WITH_OFFSET),
    };
  }

  if (!selectedCalendarDate) {
    return { startDateTime: '', endDateTime: '' };
  }

  const baseDate = getDateByTimezone(selectedCalendarDate, DATE_DISPLAY_FORMAT, timezone);

  let startDate: dayjs.Dayjs;
  let endDate: dayjs.Dayjs;

  if (isOnboardingFlow) {
    startDate = baseDate.subtract(6, 'month').startOf('day');
    endDate = baseDate.add(6, 'month').endOf('day');
  } else {
    startDate = baseDate.startOf('day');
    endDate = baseDate.endOf('day');
  }

  return {
    startDateTime: startDate.utc().tz(timezone).format(DATETIME_FORMATS.ISO_DATETIME_WITH_OFFSET),
    endDateTime: endDate.utc().tz(timezone).format(DATETIME_FORMATS.ISO_DATETIME_WITH_OFFSET),
  };
};

//#endregion

const commonQueryOptions = {
  retry: 1,
  cacheTime: 10 * 60000, // 10 mins
  staleTime: 10 * 60000, // 10 mins,
};

type UseGetCalendarEventsV3DataNewParamsType =
  | {
      selectedLocationIds: string[];
      page?: number;
      limit?: number;
      selectedDate: string; // MM/DD/YYYY
      startDateTime?: string;
      endDateTime?: string;
      enabled?: boolean;
      includeAttendeeDetails?: boolean;
      includeOperatoryDetails?: boolean;
      isOnboardingFlow?: boolean;
    }
  | {
      selectedLocationIds: string[];
      page?: number;
      limit?: number;
      selectedDate?: string; // MM/DD/YYYY
      startDateTime: string; // YYYY-MM-DDTHH:mm:ssZ
      endDateTime: string; // YYYY-MM-DDTHH:mm:ssZ
      enabled?: boolean;
      includeAttendeeDetails?: boolean;
      includeOperatoryDetails?: boolean;
      isOnboardingFlow?: boolean;
    };

export const useGetCalendarEventsV3Data = ({
  selectedLocationIds,
  selectedDate,
  startDateTime,
  endDateTime,
  page,
  limit,
  enabled = true,
  includeAttendeeDetails = true,
  includeOperatoryDetails = true,
  isOnboardingFlow = false,
}: UseGetCalendarEventsV3DataNewParamsType) => {
  const { isScheduleV3FlagEnabled, isLoading: isLoadingFF } = Helpers.useGetSchedulerV3FeatureFlagDetails();
  const { selectedGroupId, isSingleTypeScope, accessibleLocationData } = useAppScopeStore();
  const isMultiLocation = !isSingleTypeScope;
  const getLocationTimeZone = useCallback(
    (locationId: string) => {
      const location = accessibleLocationData?.[locationId];
      return location?.timezone || '';
    },
    [accessibleLocationData]
  );

  const locationInfoListMap = useMemo(() => {
    return Object.fromEntries(
      selectedLocationIds.map((locationId) => {
        const locationTimezone = getLocationTimeZone(locationId);
        const { startDateTime: calendarStartDateTime, endDateTime: calendarEndDateTime } =
          getCalendarStartAndEndDateTime({
            selectedCalendarDate: selectedDate || '',
            timezone: locationTimezone,
            isOnboardingFlow,
            customEndDateTime: endDateTime,
            customStartDateTime: startDateTime,
          });

        return [
          locationId,
          {
            timezone: locationTimezone,
            startDateTime: calendarStartDateTime,
            endDateTime: calendarEndDateTime,
          },
        ];
      })
    );
  }, [selectedLocationIds, getLocationTimeZone, selectedDate, isOnboardingFlow, endDateTime, startDateTime]);

  const hasValidateQueryDates =
    isValidDate(dayjs(selectedDate, 'MM/DD/YYYY', true)) ||
    (isValidDate(dayjs(startDateTime)) && isValidDate(dayjs(endDateTime)));

  const queryEnabled =
    enabled && isScheduleV3FlagEnabled && !isLoadingFF && !!selectedLocationIds.length && hasValidateQueryDates;

  const singleLocationId = selectedLocationIds[0] ?? '';
  const singleLocationStartDateTime = locationInfoListMap[singleLocationId]?.startDateTime;
  const singleLocationEndDateTime = locationInfoListMap[singleLocationId]?.endDateTime;

  const singleLocationQuery = Queries.useListCalendarEventsQuery({
    request: {
      locationId: singleLocationId,
      startDateTime: singleLocationStartDateTime ?? '',
      endDateTime: singleLocationEndDateTime ?? '',
      includeAttendeeDetails,
      includeOperatoryDetails,
      ...(limit && { limit }),
      ...(page && { page }),
    },
    options: {
      ...commonQueryOptions,
      enabled:
        queryEnabled &&
        !isMultiLocation &&
        !!singleLocationId &&
        !!singleLocationStartDateTime &&
        !!singleLocationEndDateTime,
    },
  });

  const multiLocationQueries = Queries.useListCalendarEventsMultiQueries(
    selectedLocationIds.map((locationId) => ({
      request: {
        parentLocationId: selectedGroupId,
        locationsIds: [locationId],
        startDateTime: locationInfoListMap[locationId]?.startDateTime ?? '',
        endDateTime: locationInfoListMap[locationId]?.endDateTime ?? '',
        includeAttendeeDetails,
        includeOperatoryDetails,
        ...(limit && { limit }),
        ...(page && { page }),
      },
      options: {
        ...commonQueryOptions,
        enabled:
          queryEnabled &&
          isMultiLocation &&
          !!locationInfoListMap[locationId]?.startDateTime &&
          !!locationInfoListMap[locationId]?.endDateTime,
      },
    }))
  );

  const stableMultiLocationQueries = useStableQueries(multiLocationQueries);

  const computedMultiLocationData = useMemo(() => {
    const initial: Types.ListCalendarEventsMultiIO['output'] = { totalCount: 0, events: [] };
    const data = stableMultiLocationQueries.reduce((acc, res) => {
      if (res.data) {
        // NOTE: need to keep number parsing as totalCount can be a string received from the API
        acc.totalCount = (acc.totalCount || 0) + Number(res.data.totalCount || 0);
        acc.events = acc.events.concat(res.data.events || []);
      }
      return acc;
    }, initial);
    return {
      data,
      isLoading: stableMultiLocationQueries.some((res) => res.isLoading),
      isError: stableMultiLocationQueries.some((res) => res.isError),
      isFetching: stableMultiLocationQueries.some((res) => res.isFetching),
    };
  }, [stableMultiLocationQueries]);

  if (!isMultiLocation) {
    return {
      data: singleLocationQuery.data,
      isLoading: singleLocationQuery.isLoading,
      isError: singleLocationQuery.isError,
      isFetching: singleLocationQuery.isFetching,
    };
  }

  return computedMultiLocationData;
};
